import React, { useState } from 'react';
import { useApiClient } from '@marageti/z4-lib';
import { MAX_OTP_FAILED_ATTEMPTS } from '../../helpers';
import useLoginAuth from '../../use-login-auth';
import EmailVerificationView from './email-verification-view';

export interface EmailVerificationProps {
  email: string;
  cooldownSeconds: number;
  startCooldown: () => void;
  hasSmsPhone: boolean;
  onChangeEmail: () => void;
  onUsePasswordVerification: () => void;
  onUseSmsVerification: () => void | Promise<void>;
}

const EmailVerification = ({
  email,
  cooldownSeconds,
  startCooldown,
  hasSmsPhone,
  onChangeEmail,
  onUsePasswordVerification,
  onUseSmsVerification,
}: EmailVerificationProps): React.ReactElement => {
  const apiClient = useApiClient();
  const { login } = useLoginAuth();
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const showTooManyAttempts = failedAttempts >= MAX_OTP_FAILED_ATTEMPTS;

  const handleResendCode = async () => {
    if (cooldownSeconds > 0) {
      return;
    }
    setVerificationError(false);
    setFailedAttempts(0);
    startCooldown();
    await apiClient.authClient
      .postVerification({ channel: 'email', email })
      .catch(console.warn);
  };

  const handleVerify = async (code: string) => {
    if (showTooManyAttempts) {
      return;
    }
    setIsVerifying(true);
    setVerificationError(false);
    const minCheckDelay = new Promise<void>((resolve) => {
      setTimeout(resolve, 1500);
    });

    let failed = false;
    try {
      await login({ channel: 'email', email, code });
    } catch {
      failed = true;
    }

    await minCheckDelay;
    if (failed) {
      setVerificationError(true);
      setFailedAttempts((prev) => prev + 1);
    }
    setIsVerifying(false);
  };

  const handleChangeEmail = () => {
    setFailedAttempts(0);
    onChangeEmail();
  };

  return (
    <EmailVerificationView
      email={email}
      isVerifying={isVerifying}
      verificationError={verificationError}
      cooldownSeconds={cooldownSeconds}
      showSmsOption={hasSmsPhone}
      showTooManyAttempts={showTooManyAttempts}
      onVerify={handleVerify}
      onClearError={() => setVerificationError(false)}
      onResendCode={handleResendCode}
      onUseSmsVerification={onUseSmsVerification}
      onChangeEmail={handleChangeEmail}
      onUsePasswordVerification={onUsePasswordVerification}
    />
  );
};

export default EmailVerification;
